"use client";
import { useProject } from "../../../../hooks/query";
import { IProjectList } from "../../../../interfaces";
import { ProgressStepper } from "../../../../shared";
import {
    AddHomeWorkRounded,
    AddRounded,
    AutoModeRounded,
    ChecklistRounded,
    ImportExportRounded,
    Inventory2Rounded,
    KeyboardBackspaceRounded,
    ShareRounded,
} from "@mui/icons-material";
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    ListItemDecorator,
    Tab,
    tabClasses,
    TabList,
    TabPanel,
    Tabs,
    Typography,
    IconButton as JoyIconButton,
    Dropdown,
    MenuButton,
    Menu,
    MenuItem,
} from "@mui/joy";
import { PackageCheck, ChevronDown, BookX, ListTodo, Plus, Building2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";

import { Skeleton, Stack } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";
import { Alert } from "../../../../shared";

export function SummaryCard() {
    const params = useParams();
    const id = params.id as string;

    const { useListById } = useProject();
    const listProjectByIdSvc = useListById();

    const [projectDetails, setProjectDetails] = useState<IProjectList | null>(null);
    const [hasLoaded, setHasLoaded] = useState(false);

    const fetchData = async () => {
        try {
            const response = await listProjectByIdSvc.mutateAsync({ id });
            setProjectDetails(response);
            setHasLoaded(true);
        } catch (e) {
            console.error("Failed to fetch project:", e);
        }
    };

    useEffect(() => {
        if (id) {
            fetchData();
        }
    }, [id]);

    const isLoading = listProjectByIdSvc.isPending && !hasLoaded;

    if (listProjectByIdSvc.isError) {
        return <Alert errorMessage={(listProjectByIdSvc.error as any)?.message || "Failed to load project summary"} />;
    }

    return (
        <AnimatePresence mode="wait">
            {isLoading ? (
                <motion.div
                    key="skeleton"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <Box display={"flex"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Box display={"flex"} gap={1} alignItems={"center"}>
                            <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: "md" }} />
                            <Stack spacing={0.5}>
                                <Skeleton variant="text" width={60} height={12} />
                                <Skeleton variant="text" width={180} height={24} />
                            </Stack>
                        </Box>
                        <Box display={"flex"} gap={2}>
                            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: "md" }} />
                            <Skeleton variant="rectangular" width={160} height={36} sx={{ borderRadius: "md" }} />
                        </Box>
                    </Box>
                </motion.div>
            ) : (
                <motion.div
                    key="content"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <Box>
                        <Box display={"flex"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                            <Box display={"flex"} gap={1} alignItems={"center"}>
                                <Card sx={{ p: 1 }} variant="solid">
                                    <Building2 size={24} strokeWidth={1.4} absoluteStrokeWidth />
                                </Card>
                                <Box>
                                    <Typography level={"body-sm"}>Project</Typography>
                                    <Typography level={"title-lg"}>{projectDetails?.Name || "-"}</Typography>
                                </Box>
                            </Box>

                            <Box display={"flex"} gap={2} alignItems={"center"}>
                                <Button variant="outlined" color="neutral" startDecorator={<AddRounded />}>
                                    Follow
                                </Button>
                                <Dropdown>
                                    <ButtonGroup aria-label="new project button" variant="outlined" color="danger">
                                        <Button startDecorator={<PackageCheck size={18} />}>Mark as complete</Button>
                                        <MenuButton slots={{ root: JoyIconButton }}>
                                            <ChevronDown size={18} />
                                        </MenuButton>
                                    </ButtonGroup>
                                    <Menu placement="bottom-end" sx={{ width: 210 }}>
                                        <MenuItem>
                                            <BookX size={18} />
                                            Mark as cancelled
                                        </MenuItem>
                                        <MenuItem>
                                            <ListTodo size={18} />
                                            Mark as incomplete
                                        </MenuItem>
                                    </Menu>
                                </Dropdown>
                            </Box>
                        </Box>
                    </Box>
                </motion.div>
            )}
        </AnimatePresence>
    );
    return (
        <Card variant="outlined" sx={{ background: "##f3f3ed" }}>
            <Box display={"flex"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                <Box display={"flex"} gap={1} alignItems={"center"}>
                    <Card sx={{ p: 1 }} variant="solid">
                        <AddHomeWorkRounded sx={{ fontSize: 24 }} />
                    </Card>
                    <Box>
                        <Typography level={"body-sm"}>Project</Typography>
                        <Typography level={"title-lg"}>Residential Roof Replacement</Typography>
                    </Box>
                </Box>

                <Box display={"flex"} gap={1} alignItems={"center"}>
                    <Button variant="outlined" color="neutral" startDecorator={<AddRounded />}>
                        Follow
                    </Button>
                    <ButtonGroup variant="outlined" aria-label="Basic button group">
                        <Button>New Task</Button>
                        <Button>New Material</Button>
                        <Button>New Inspection</Button>
                        <Button>New Note</Button>
                    </ButtonGroup>
                </Box>
            </Box>
            <Divider />

            <Box display={"flex"} gap={4} width={"100%"} justifyContent={"space-between"}>
                <Box display={"flex"} gap={4} maxWidth={700} width={"100%"} justifyContent={"space-between"}>
                    <MetaData propertyName={"Phone Number"} propertyValue={"+1-555-1234"} />
                    <MetaData propertyName={"Manager"} propertyValue={"John Smith"} />
                    <MetaData propertyName={"Start Date"} propertyValue={"2023-01-15"} />
                    <MetaData propertyName={"Estimated Completion"} propertyValue={"2023-06-30"} />
                </Box>
                <Box>
                    <Dropdown>
                        <ButtonGroup aria-label="new project button" variant="solid" color="primary">
                            <Button startDecorator={<PackageCheck size={18} />}>Mark as complete</Button>
                            <MenuButton slots={{ root: JoyIconButton }}>
                                <ChevronDown size={18} />
                            </MenuButton>
                        </ButtonGroup>
                        <Menu placement="bottom-end" sx={{ width: 210 }}>
                            <MenuItem>
                                <BookX size={18} />
                                Mark as cancelled
                            </MenuItem>
                            <MenuItem>
                                <ListTodo size={18} />
                                Mark as incomplete
                            </MenuItem>
                        </Menu>
                    </Dropdown>
                </Box>
            </Box>
        </Card>
    );
}

function MetaData(props: any) {
    const { propertyName, propertyValue }: any = { ...props };

    return (
        <Box>
            <Typography level={"body-sm"} sx={{ color: "text.primary" }}>
                {propertyName}
            </Typography>
            <Typography level={"body-sm"} sx={{ fontWeight: 600, color: "text.primary" }}>
                {propertyValue}
            </Typography>
        </Box>
    );
}
